// Client for QUERY /api/audio/extraction, which runs the Nidus-iOS transcript
// extraction engine (port) server-side. The voice page sends the ordered
// utterance segments of the live transcript and renders the returned data
// extractions.

export interface ExtractionField {
	key: string;
	label: string;
	value: string;
	unit?: string;
	derived: boolean;
	anomalous: boolean;
	/**
	 * The amount was spoken as an estimate ("about 4 oz", "roughly ten by
	 * fifteen feet"); absent when stated exactly, so its absence is what marks
	 * an exact value.
	 */
	approximate?: boolean;
	attribution?: string;
}

/** Inclusive word-index range into ExtractionContext.words. */
export interface WordRange {
	start: number;
	end: number;
}

/**
 * A detected context span (inclusive global word indices): one opened by a
 * "begin <name>" marker, or by a natural-language phrase that announces the
 * context ("make an inspection").
 */
export interface ContextSpan {
	name: string;
	start: number;
	end: number;
}

/** A context-scoped boolean tag (inclusive global word indices). */
export interface ContextBoolean {
	context: string;
	key: string;
	value: boolean;
	start: number;
	end: number;
}

/**
 * A treatment product's identity, the three facets a treatment keeps apart: the
 * STATED form (what the technician said, in the catalog's spelling), the FULL
 * NAME the product is known by when that is longer or clearer than the spoken
 * form, and the GENERIC term describing what the product actually is. A
 * generic-only control (biological control, source reduction) carries no
 * `fullName`; a product with no generic version (a proprietary blend such as
 * BVA Oil) carries no `generic`. A treatment whose product is unstated carries
 * no identity at all. Render it with `productText`.
 */
export interface ProductIdentity {
	stated: string;
	fullName?: string;
	generic?: string;
}

/** A context-scoped enumerated name/value pair (inclusive global word indices). */
export interface ContextEnumeration {
	context: string;
	key: string;
	value: string;
	/**
	 * Identity behind the value, on the treatment `product` enumeration only:
	 * `value` is the stated form the words resolved to, and this carries the
	 * full name and generic term that would otherwise be lost. Absent on every
	 * other enumeration.
	 */
	product?: ProductIdentity;
	start: number;
	end: number;
}

/** A context-scoped numerical extraction (inclusive global word indices). */
export interface ContextNumber {
	context: string;
	field: string;
	count: number;
	unit: string;
	/**
	 * True when the count was spoken as an estimate ("about 4 ounces",
	 * "roughly 10 dips"); absent when it was stated exactly, so a count is
	 * never marked approximate by omission.
	 */
	approximate?: boolean;
	start: number;
	end: number;
}

/** A context-scoped free-text capture (inclusive global word indices). */
export interface ContextFreeText {
	context: string;
	key: string;
	text: string;
	start: number;
	end: number;
}

/** A context-scoped negation capture (inclusive global word indices). */
export interface ContextNegation {
	context: string;
	start: number;
	end: number;
}

/** A context-scoped free-text description (inclusive global word indices). */
export interface ContextDescription {
	context: string;
	text: string;
	start: number;
	end: number;
}

/**
 * Local transcript text that produced an extraction: a short window of words
 * around the trigger, with trigger words marked by index ranges into words.
 */
export interface ExtractionContext {
	words: string[];
	highlights: WordRange[];
	/**
	 * Global word index of words[0] in the engine's whole-transcript word
	 * stream. A highlight's global range is
	 * [start + highlight.start, start + highlight.end], which is what lets the
	 * UI box the words that produced the extraction.
	 */
	start: number;
}

/** One engine token inside an utterance segment. */
export interface UtteranceWord {
	/** Engine token text (lowercased, sentence punctuation split out). */
	text: string;
	/** Inclusive code-point offset of the token's surface in UtteranceSpan.text, or -1. */
	start: number;
	/** Inclusive code-point offset of the token's surface in UtteranceSpan.text, or -1. */
	end: number;
}

/**
 * The slice of the engine's global word stream one request utterance segment
 * occupies. `start`/`end` are inclusive global word indices (-1 for a segment
 * with no tokens), `text` is the segment as tokenized, and `words` carries the
 * per-token code-point offsets into `text` so the UI can box extraction words
 * over the original surface text. Spans are index-aligned with the `utterances`
 * sent to the endpoint.
 */
export interface UtteranceSpan {
	start: number;
	end: number;
	text: string;
	words: UtteranceWord[];
}

export interface ExtractionRecord {
	kind: string;
	label: string;
	summary: string;
	startSeconds: number | null;
	endSeconds: number | null;
	fields: ExtractionField[];
	context?: ExtractionContext;
}

/**
 * An alternative-surface-expression capture over the global word stream:
 * a vocabulary-free projection onto an ontology concept (and optional
 * attribute/value) anchored to an inclusive word-index span.
 */
export interface Meaning {
	concept: string;
	attribute?: string;
	value?: string;
	start: number;
	end: number;
}

/**
 * A spoken size specification over the global word stream: two spoken
 * dimensions are a surface area (a footprint), three are a volume. `depth` is
 * absent for a surface area, and `unit` is empty when the dimensions carry
 * mixed units. Mirrors the engine's Measurement.
 */
export interface Measurement {
	/** "surfaceArea" | "volume" — the size the dimensions describe. */
	key: string;
	/** Human label, e.g. "Surface area" or "Volume". */
	label: string;
	/**
	 * Length and width are always set. A dimension the technician named claims
	 * its own slot; the rest fill the empty slots in spoken order, so an
	 * unqualified specification carries first → length, second → width.
	 */
	length: number;
	width: number;
	/** Third dimension; absent for a surface area. */
	depth?: number;
	/** The dimensions as joined for display, e.g. "10 by 15 by 3". */
	value: string;
	/**
	 * Unit all dimensions share, e.g. "feet"; ABSENT (not "") when they carry
	 * mixed units, in which case `value` spells each dimension's own unit.
	 */
	unit?: string;
	/**
	 * True when the dimensions were spoken as an estimate ("about ten by
	 * fifteen by three feet"); absent when they were stated exactly, so a size
	 * is never marked approximate by omission.
	 */
	approximate?: boolean;
	/** Inclusive global word index of the first dimension value. */
	start: number;
	/** Inclusive global word index of the last unit/dimension. */
	end: number;
}

/**
 * Mark a rendered amount the transcript hedged as an estimate: a leading `~`
 * ("~10 by 15 by 3 feet", "~4 ounces"), and the text untouched when the measure
 * was stated exactly. ONE rule for every amount the voice page shows — the
 * extraction table rows, the session-tree attribute cells and the transcript
 * tooltips — so an estimate reads the same in all three and an exactly stated
 * value stays byte-identical to what it rendered before the engine reported
 * the flag.
 */
export function approximateText(text: string, approximate?: boolean): string {
	return approximate ? `~${text}` : text;
}

/**
 * Render a measurement's amounts for display: the joined dimensions, with the
 * dimensions' shared unit appended when they have one. Mixed-unit measurements
 * spell each dimension's own unit inside `value`, so nothing is appended and
 * the text never reads "… inches inches". Takes just the display fields, which
 * both the extract-level `Measurement` and a knowledge-view attribute's
 * `measurement` carry. A hedged size is marked by the caller through
 * `approximateText`, not here, so the amounts themselves render the same
 * however the estimate is displayed.
 */
export function measurementText(
	measurement: Pick<Measurement, "value" | "unit">,
): string {
	return `${measurement.value}${measurement.unit ? " " + measurement.unit : ""}`;
}

/**
 * Render a treatment product's identity for display, keeping its three facets
 * apart in one line:
 *
 *     <stated>[ (<full name>)][ [generic: <generic>]]
 *
 * The stated form always leads. The full name follows in parentheses when it is
 * present AND differs from the stated form, and the generic term follows in
 * square brackets marked `generic:` when it is present and differs from the
 * stated form: "BVA Oil (BVA 2 Mosquito Larvicide Oil)", "Natular DT [generic:
 * Spinosad]". A facet that repeats the stated form is dropped, so a generic-only
 * control never reads "Methoprene (Methoprene)" and an abbreviation whose
 * catalog spelling is already its full name gets no empty parentheses. The two
 * distinct brackets are what keep the facets unambiguous when a product carries
 * both: parentheses are always the full name, the `generic:` bracket is always
 * the generic term. Takes just the identity fields, which both the extract-level
 * `ContextEnumeration` and a knowledge-view attribute's `product` carry.
 */
export function productText(product: ProductIdentity): string {
	const fullName =
		product.fullName && product.fullName !== product.stated
			? ` (${product.fullName})`
			: "";
	const generic =
		product.generic && product.generic !== product.stated
			? ` [generic: ${product.generic}]`
			: "";
	return `${product.stated}${fullName}${generic}`;
}

/** One renderable field of a knowledge attribute, in display order. */
export interface KnowledgeAttributePart {
	/** Which attribute field this renders; the cell styles it as `attribute-<kind>`. */
	kind: "measurement" | "value" | "text" | "count" | "unit";
	/** The text to display. */
	text: string;
}

/**
 * The renderable fields of a knowledge attribute for the voice page's
 * session-tree cell, in display order.
 *
 * A size attribute (`volume`, `surface_area`) carries its amounts only inside
 * `measurement`: its own `value` is the ontology value ID and is empty for a
 * size, while `unit` repeats the measurement's unit — so the measurement is the
 * one part rendered and the other fields are dropped, which is what stops the
 * cell from either hiding the size or reading "10 by 15 by 3 feet feet". A
 * treatment `product` attribute carries its identity in `product`: its `value`
 * is the ontology value ID (`bva_oil`), so the identity renders in the value
 * part instead and the cell reads "BVA Oil (BVA 2 Mosquito Larvicide Oil)"
 * rather than the bare ID. Every other attribute renders the fields the
 * extraction filled in, with free text quoted so prose is distinguishable from
 * a vocabulary value. An amount the transcript hedged renders through
 * `approximateText`, so the cell marks the estimate with the same `~` the
 * extraction table and the transcript tooltips use.
 */
export function knowledgeAttributeParts(
	attribute: KnowledgeViewAttribute,
): KnowledgeAttributePart[] {
	if (attribute.measurement) {
		return [
			{
				kind: "measurement",
				text: approximateText(
					measurementText(attribute.measurement),
					attribute.approximate,
				),
			},
		];
	}
	const parts: KnowledgeAttributePart[] = [];
	if (attribute.product) {
		parts.push({ kind: "value", text: productText(attribute.product) });
	} else if (attribute.value) {
		parts.push({
			kind: "value",
			text: approximateText(attribute.value, attribute.approximate),
		});
	}
	if (attribute.text) {
		parts.push({ kind: "text", text: `"${attribute.text}"` });
	}
	if (attribute.count !== undefined) {
		parts.push({
			kind: "count",
			text: approximateText(String(attribute.count), attribute.approximate),
		});
	}
	if (attribute.unit) {
		parts.push({ kind: "unit", text: attribute.unit });
	}
	return parts;
}

/**
 * The spoken site address over the global word stream: the address as one
 * free-text value (e.g. "2345 West Cherry Court"), never decomposed into
 * house-number/street/locality fields, with `start`/`end` the inclusive global
 * word indices of its first and last word. An address is a SITE attribute, so
 * it is captured context-free: it is its own layer, not a record in
 * `extractions` and not a `contextFreeText` capture.
 */
export interface Address {
	/**
	 * The spoken words as one free-text value, rendered in an address's
	 * written form: each word capitalized, nothing abbreviated or dropped.
	 */
	text: string;
	/** Inclusive global word index of the first address word. */
	start: number;
	/** Inclusive global word index of the last address word. */
	end: number;
}

/** Three-valued capture state for a knowledge-view concept or attribute. */
export type TriState = "unknown" | "present" | "notPresent";

/** A knowledge-view attribute projected from one extraction fact. */
export interface KnowledgeViewAttribute {
	attribute: string;
	label: string;
	state: TriState;
	value?: string;
	valueLabel?: string;
	/**
	 * Identity of a treatment `product` attribute: the stated form, full name
	 * and generic term the ontology value ID (`value`, e.g. `bva_oil`) cannot
	 * carry. The session-tree cell renders THIS through `productText` instead of
	 * `value`. Only present on that one attribute, and absent when the
	 * transcript left the product unstated.
	 */
	product?: ProductIdentity;
	text?: string;
	count?: number;
	unit?: string;
	/**
	 * Size projection of a `volume` or `surface_area` attribute: the amounts the
	 * transcript carried, joined the way they were spoken, plus the unit when the
	 * dimensions share one. Only present on those two attributes. The attribute's
	 * own `unit` repeats this unit for consumers that read count/unit alone, so a
	 * consumer rendering the size renders THIS — not `value`/`unit`, which alone
	 * carry no amounts (the defect this fixes) and together double the unit.
	 */
	measurement?: {
		length: number;
		width: number;
		/** Third dimension; absent for a surface area. */
		depth?: number;
		/** The amounts joined for display, e.g. "10 by 15 by 3". Always set. */
		value: string;
		/** Shared unit of the dimensions; absent when they differ. */
		unit?: string;
	};
	/**
	 * True when the attribute's amount — the size in `measurement`, or the
	 * `count` of a treatment amount — was spoken as an estimate ("about ten by
	 * fifteen by three feet", "approximately 4 oz"); absent when it was stated
	 * exactly, so an attribute is never marked approximate by omission.
	 */
	approximate?: boolean;
	start: number;
	end: number;
}

/** A knowledge-view ontology concept with its projected attributes. */
export interface KnowledgeViewConcept {
	concept: string;
	label: string;
	state: TriState;
	attributes: KnowledgeViewAttribute[];
	/**
	 * Free-text descriptions observed for the concept: the maximal runs of
	 * unmatched prose inside its context span, in transcript order, never
	 * null/undefined — an empty array when the transcript described nothing. A
	 * list of strings because an attribute ID holds only one attribute row while
	 * a context may be described many times.
	 */
	descriptions: string[];
}

/** Vocabulary-free knowledge view projected from an ExtractionResult. */
export interface KnowledgeView {
	concepts: KnowledgeViewConcept[];
}

/**
 * One node of the session tree: an ontology concept (context span, negation,
 * or implicit fact host) carrying its projected attributes and child concepts.
 * Start/End are inclusive word indices, -1 when the node is implicit.
 */
export interface SessionTreeNode {
	id: string;
	kind: string;
	label: string;
	state: TriState;
	start: number;
	end: number;
	attributes: KnowledgeViewAttribute[];
	/**
	 * Free-text descriptions observed for the node: the maximal runs of
	 * unmatched prose inside its context span, in transcript order, never
	 * null/undefined — an empty array when the transcript described nothing. A
	 * list of strings because an attribute ID holds only one attribute row while
	 * a context may be described many times.
	 */
	descriptions: string[];
	children: SessionTreeNode[];
}

/** Hierarchical session view projected from an ExtractionResult. */
export interface SessionTree {
	root: SessionTreeNode;
}

/** One rendered row of the session tree: a node and its indent depth. */
export interface SessionTreeRow {
	node: SessionTreeNode;
	depth: number;
}

/**
 * Flatten the session tree depth-first into indented rows. The tree is the
 * hierarchical view of the knowledge graph — the flat `knowledgeView` concepts
 * describe the same facts, so the UI renders only this.
 */
export function sessionTreeRows(
	root: SessionTreeNode | null,
): SessionTreeRow[] {
	const rows: SessionTreeRow[] = [];
	const walk = (node: SessionTreeNode, depth: number) => {
		rows.push({ node, depth });
		for (const child of node.children) {
			walk(child, depth + 1);
		}
	};
	if (root) {
		walk(root, 0);
	}
	return rows;
}

/**
 * A prompt-worthy knowledge gap: the concept (optionally one attribute) whose
 * state the transcript leaves unstated, with the rule reason that surfaced it.
 */
export interface Gap {
	concept: string;
	attribute?: string;
	reason: string;
}

/**
 * A knowledge fact implied by the session tree rather than stated directly:
 * the implied concept/attribute, the rule reason, and the concept whose
 * presence implies it.
 */
export interface ImpliedFact {
	concept: string;
	attribute?: string;
	reason: string;
	source: string;
}

/**
 * Derived understanding read over the session tree: which top-level facts the
 * transcript supports, plus the number of open gaps inference surfaced.
 */
export interface DerivedUnderstanding {
	hasSource: boolean;
	hasBreeding: boolean;
	hasTreatment: boolean;
	openGaps: number;
}

/**
 * Knowledge-level inference derived from the session tree: the understanding
 * read, the prompt-worthy gaps, and the facts implied by what was said.
 * Structural only — it never reinterprets the raw extraction.
 */
export interface Inference {
	understanding: DerivedUnderstanding;
	gaps: Gap[];
	implied: ImpliedFact[];
}

/** Procedural effect kinds proposed by the server (S4). Two are reserved. */
export type ProceduralKind =
	| "inventory_change"
	| "follow_up_visit"
	| "service_request"
	| "compliance_report";

/** One server-side procedural proposal derived from the session tree. */
export interface ProceduralEffect {
	kind: ProceduralKind;
	reason: string;
	/** Ontology concept the effect derived from (e.g. "treatment"). */
	concept?: string;
	/** Ontology product value ID the effect consumes, when one applies. */
	product?: string;
	amount?: number;
	unit?: string;
}

/** Procedural proposals derived from the accepted knowledge graph. */
export interface ProceduralProgram {
	effects: ProceduralEffect[];
}

/** Symbolic prompt kinds derived from the session tree (S5). */
export type PromptKind = "complete" | "implied" | "situation";

/**
 * One symbolic prompt: the concept (optionally one attribute) the session
 * should cover, the rule reason that surfaced it, and its sort priority.
 * `complete` prompts name an attribute left unstated in a context; `implied`
 * prompts name a fact implied by the tree, whose `source` names the concept
 * whose presence implied it; `situation` prompts carry a `situation` ID instead
 * and direct the technician to act on the situation the tree shows. Symbolic
 * only — the UI words it.
 */
export interface Prompt {
	concept: string;
	attribute?: string;
	/**
	 * Situation ID of a `situation` prompt — the directive to act on (e.g.
	 * "property-access"); absent on every concept prompt.
	 */
	situation?: string;
	kind: PromptKind;
	reason: string;
	priority: number;
	source?: string;
}

/**
 * Ordered prompts derived from the session tree and inference (S5): index 0 is
 * the prompt to act on, the remainder are lower-priority reminders.
 */
export interface PromptList {
	prompts: Prompt[];
}

export interface ExtractionResult {
	extractions: ExtractionRecord[];
	contexts: ContextSpan[];
	currentContext: string | null;
	contextBooleans: ContextBoolean[];
	contextEnumerations: ContextEnumeration[];
	contextNumbers: ContextNumber[];
	contextFreeText: ContextFreeText[];
	contextNegations: ContextNegation[];
	/** Free-text descriptions: span words no recognized layer consumed. */
	descriptions: ContextDescription[];
	/**
	 * Custom tags captured from the transcript ("tag as <X>"), in capture
	 * order, de-duplicated by text. Each tag also appears as a tag extraction
	 * record; this is the flat list the page renders.
	 */
	tags: string[];
	meanings: Meaning[];
	/** Spoken size specifications — at most one: a surface area or a volume. */
	measurements: Measurement[];
	/**
	 * The site's spoken address — one free-text value attached to the site,
	 * never decomposed; the last address spoken wins, and it is null when the
	 * transcript named none.
	 */
	address: Address | null;
	/** Global word span + token offsets of each utterance segment sent. */
	utteranceSpans: UtteranceSpan[];
	knowledgeView: KnowledgeView;
	sessionTree: SessionTree;
	/** Knowledge-level inference derived from the session tree (S3). */
	inference: Inference;
	/** Procedural proposals derived from the knowledge graph (S4). */
	procedural: ProceduralProgram;
	/** Ordered symbolic prompts derived from the session tree (S5). */
	prompts: PromptList;
}

export interface ContextWord {
	text: string;
	mark: boolean;
}

/**
 * Expand a context's word list into renderable words, marking every word that
 * falls inside a highlight range. Ranges are clamped to the word-list bounds.
 */
export function contextWords(context: ExtractionContext): ContextWord[] {
	const marked = new Array<boolean>(context.words.length).fill(false);
	for (const range of context.highlights) {
		const start = Math.max(0, range.start);
		const end = Math.min(context.words.length - 1, range.end);
		for (let i = start; i <= end; i++) {
			marked[i] = true;
		}
	}
	return context.words.map((text, index) => ({ text, mark: marked[index] }));
}

/**
 * Send the current utterance segments to the server extraction engine.
 *
 * The endpoint uses the HTTP QUERY method (RFC 10008): a safe, idempotent read
 * whose query travels as a JSON body instead of a URL-escaped query parameter.
 * Utterance segmentation is decided client-side (a final transcript before an
 * utterance_end belongs to the same segment), so the segments ship as the body's
 * `utterances` array.
 */
export async function extractUtterances(
	utterances: string[],
): Promise<ExtractionResult> {
	const response = await fetch("/api/audio/extraction", {
		method: "QUERY",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ utterances }),
	});
	if (!response.ok) {
		throw new Error(`Extraction request failed (${response.status})`);
	}
	return (await response.json()) as ExtractionResult;
}

/** The extraction layers the table colours differently. */
export type ExtractionType =
	| "boolean"
	| "enumeration"
	| "number"
	| "free-text"
	| "negation"
	| "meaning"
	| "measurement"
	| "address";

/** One row of the extraction table: a key, its value, and its provenance. */
export interface ExtractionRow {
	/** Stable identity for list rendering. */
	id: string;
	/** Which layer produced the row; drives its colour. */
	type: ExtractionType;
	/** The extraction's key name — a field key, concept path, or context. */
	key: string;
	/** The captured value, formatted for display. */
	value: string;
	/** Context the extraction belongs to, when the layer is context-scoped. */
	context?: string;
	/** Global word index the extraction starts at. */
	start: number;
}

/**
 * Flatten every extraction layer into table rows, ordered by where each
 * extraction appears in the transcript (the engine's global word indices).
 * The layers are the context-scoped booleans, enumerations, numbers, free
 * text and negations, the meanings, the spoken size measurements, and the
 * site address. Rows starting at the same word fall back to key then layer
 * order, so the table is stable across renders. The knowledge view and session
 * tree are not consulted — they are projections of these same facts and have
 * their own section. A hedged size or number keeps its estimate, marked with
 * the same `~` the knowledge cells and transcript tooltips use, so the table
 * reads an approximation as one.
 */
export function extractionRows(
	result: ExtractionResult | null,
): ExtractionRow[] {
	if (!result) {
		return [];
	}
	const rows: ExtractionRow[] = [];
	const push = (row: Omit<ExtractionRow, "id">) => {
		rows.push({
			...row,
			id: `${row.type}:${row.start}:${row.key}:${row.value}`,
		});
	};

	for (const item of result.contextBooleans ?? []) {
		push({
			type: "boolean",
			key: item.key,
			value: item.value ? "true" : "false",
			context: item.context,
			start: item.start,
		});
	}
	for (const item of result.contextEnumerations ?? []) {
		push({
			type: "enumeration",
			key: item.key,
			// A treatment product carries an identity behind its stated value, so
			// the row shows all three facets the way the knowledge cells do;
			// every other enumeration shows the value the engine captured.
			value: item.product ? productText(item.product) : item.value,
			context: item.context,
			start: item.start,
		});
	}
	for (const item of result.contextNumbers ?? []) {
		push({
			type: "number",
			key: item.field,
			value: approximateText(
				[String(item.count), item.unit]
					.filter((part) => part.length > 0)
					.join(" "),
				item.approximate,
			),
			context: item.context,
			start: item.start,
		});
	}
	for (const item of result.contextFreeText ?? []) {
		push({
			type: "free-text",
			key: item.key,
			value: item.text,
			context: item.context,
			start: item.start,
		});
	}
	for (const item of result.contextNegations ?? []) {
		push({
			type: "negation",
			key: item.context,
			value: "no findings",
			context: item.context,
			start: item.start,
		});
	}
	for (const meaning of result.meanings ?? []) {
		push({
			type: "meaning",
			key: meaning.attribute
				? `${meaning.concept}.${meaning.attribute}`
				: meaning.concept,
			value: meaning.value ?? "—",
			start: meaning.start,
		});
	}

	for (const measurement of result.measurements ?? []) {
		push({
			type: "measurement",
			key: measurement.key,
			value: approximateText(
				measurementText(measurement),
				measurement.approximate,
			),
			start: measurement.start,
		});
	}

	// The site address is its own layer: one free-text value, keyed "address"
	// so the row reads as the site attribute it is.
	if (result.address) {
		push({
			type: "address",
			key: "address",
			value: result.address.text,
			start: result.address.start,
		});
	}

	return rows.sort(
		(a, b) =>
			a.start - b.start ||
			a.key.localeCompare(b.key) ||
			a.type.localeCompare(b.type),
	);
}
